import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, isBackendConfigured } from '../utils/backendApi';
import { FeatureStatusChip } from './Features';

// /admin/features — the super admin's queue (GET /features?full=1,
// operator-gated), sorted by votes. Each row opens /admin/features/:id
// where the request is triaged, planned, built and promoted.
const FILTERS = [
  ['triage', 'Needs triage'],
  ['accepted', 'Accepted'],
  ['planned', 'Planned'],
  ['building', 'Building'],
  ['uat', 'On UAT'],
  ['shipped', 'Shipped'],
  ['later', 'Later'],
  ['declined', 'Declined'],
  ['all', 'All'],
];
const GROUPS = {
  triage: ['new'],
  accepted: ['accepted', 'planning'],
  planned: ['planned'],
  building: ['building', 'pr'],
};

function when(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

export default function AdminFeatures() {
  const [filter, setFilter] = useState('triage');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isBackendConfigured()) { setError('The API is not configured.'); return undefined; }
    let stop = false;
    apiGet('/features?full=1').then((d) => { if (!stop) { setData(d); setError(''); } }).catch((err) => { if (!stop) setError(err.message); });
    return () => { stop = true; };
  }, []);

  const rows = (data?.features || []).filter((f) => {
    if (filter === 'all') return true;
    const group = GROUPS[filter];
    return group ? group.includes(f.status) : f.status === filter;
  });

  return (
    <div className="cs-store">
      <header className="cs-store-hero">
        <h1>Feature requests</h1>
        <p>
          Asked for by members of every organization and voted on at <Link to="/features">/features</Link>. New requests are mailed to {data?.notifyTo || '…'}.
          Open one to triage it, have the Cursor agent draft a plan on <span className="aa-mono">{data?.repo || '…'}</span>, approve the plan into a PR, merge &amp; build to UAT, then approve &amp; promote.
        </p>
      </header>
      <section className="cs-store-section cs-support-desk">
        <div className="cs-sw-card">
          <div className="bug-list-head">
            <h3>Queue</h3>
            <Link to="/admin/apps" className="cs-help">Releases tab →</Link>
          </div>
          <div className="bug-queue-filters" role="tablist" aria-label="Filter">
            {FILTERS.map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={filter === id} className={filter === id ? 'is-on' : ''} onClick={() => setFilter(id)}>{label}</button>
            ))}
          </div>
          {error && <p className="cs-land-micro" role="alert">{error}</p>}
          {!data && !error && <p className="cs-help">Loading…</p>}
          {data && rows.length === 0 && <p className="cs-help">Nothing here.</p>}
          <ul className="cs-support-list">
            {rows.map((f) => (
              <li key={f.id}>
                <Link to={`/admin/features/${f.id}`} className="cs-support-row bug-row fr-row" style={{ display: 'flex', textDecoration: 'none' }}>
                  <span className="fr-vote is-static"><span aria-hidden="true">▲</span><strong>{f.voteCount || 0}</strong></span>
                  <div className="fr-row-main">
                    <div className="bug-row-top">
                      <strong>{f.title || 'Feature request'}</strong>
                      <FeatureStatusChip status={f.status} />
                    </div>
                    <span className="cs-help">
                      {f.name || f.email}{f.orgName ? ` · ${f.orgName}` : ''} · {when(f.createdAt)}
                      {f.attachments?.length ? ` · ${f.attachments.length} file${f.attachments.length === 1 ? '' : 's'}` : ''}
                      {f.fix?.prUrl ? ` · PR #${f.fix.prNumber || ''}` : ''}
                      {f.fix?.status === 'running' || f.fix?.status === 'starting' ? ' · agent working' : ''}
                      {f.fix?.lastError ? ' · agent error' : ''}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
